import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { PaginationState, SortingState } from "@tanstack/react-table";
import { DataTable } from "@/components/ui/customs/data-table/data-table";
import { GetAllExpeditionBoResponseDtoExpedition } from "@/types/api/api-types";
import { ActionDataBo } from "@/types/tableAction";
import { useGetCustoms } from "@/api/customs.api";
import {
  finalizedAtColumn,
  pickupDateColumn,
  identifierColumn,
  deliveryStatusBadgeColumn,
  trackingColumn,
  clientColumn,
  clientDestColumn,
  countryColumn,
  btlsVolColumn,
  incotermColumn,
  blockedReasonColumn,
  blockedAtColumn,
  envoiColumn,
  expeditionRowClassname,
} from "@/components/backOffice/shared/expeditionColumns";

type Props = {
  data: GetAllExpeditionBoResponseDtoExpedition[];
  rowCount: number;
  pagination: PaginationState;
  setPagination: React.Dispatch<React.SetStateAction<PaginationState>>;
  sorting: SortingState;
  setSorting: React.Dispatch<React.SetStateAction<SortingState>>;
  setActionData: (actionData: ActionDataBo) => void;
  isLoading?: boolean;
};

export const FailedExpeditionsTable = ({
  data,
  rowCount,
  pagination,
  setPagination,
  sorting,
  setSorting,
  setActionData,
  isLoading,
}: Props) => {
  const { t } = useTranslation("boExpeditions");
  const { data: customs = [] } = useGetCustoms();
  const columns = useMemo(
    () => [
      finalizedAtColumn(setActionData),
      identifierColumn(setActionData, { hideDraft: true }),
      envoiColumn(),
      trackingColumn(),
      deliveryStatusBadgeColumn(),
      blockedAtColumn({ header: t("failedExpeditionsTable.failedAtHeader") }),
      blockedReasonColumn({ header: t("failedExpeditionsTable.failureReasonHeader") }),
      clientColumn(),
      clientDestColumn(),
      countryColumn(customs),
      btlsVolColumn(),
      incotermColumn(),
      pickupDateColumn(),
    ],
    [setActionData, customs, t],
  );

  return (
    <DataTable
      data={data}
      columns={columns}
      pagination={pagination}
      setPagination={setPagination}
      sorting={sorting}
      setSorting={setSorting}
      rowCount={rowCount}
      autoResetPageIndex={false}
      isLoading={isLoading}
      skeletonRowClassName="h-18"
      rowClassname={(row) => expeditionRowClassname(row, "h-18")}
      headerClassName="bg-gray-300"
      headCellClassName="h-12 uppercase text-xs font-semibold tracking-wide text-gray-800 py-2 px-3"
      onRowClick={(row) => setActionData({ expedition: row, action: "DETAIL" })}
    />
  );
};
